import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

//import images
import logoModern from "../assets/images/logo-modern.svg";

//import Components
import NotificationBell from '../Components/Notifications/NotificationBell';
import ProfileDropdown from '../Components/Common/ProfileDropdown';
import { useProfile } from '../Components/Hooks/UserHooks';

import { layoutModeTypes } from '../Components/constants/layout';

const Header = ({ onChangeLayoutMode, layoutModeType, headerClass }) => {
    const location = useLocation();
    const { userProfile } = useProfile();
    
    // Check if user is admin
    const isAdmin = userProfile?.isAdmin;
    
    // Main navigation tabs - Admin vs Regular User
    const navTabs = isAdmin ? [
        { label: 'Overview', path: '/admin', icon: 'ri-dashboard-2-fill', exact: true },
        { label: 'Users', path: '/admin/users', icon: 'ri-group-fill' },
        { label: 'Payments', path: '/admin/payments', icon: 'ri-wallet-fill' },
        { label: 'Support Tickets', path: '/admin/support-tickets', icon: 'ri-customer-service-2-fill' },
        { label: 'Plan Upgrades', path: '/admin/plan-upgrade-requests', icon: 'ri-vip-crown-fill' },
    ] : [
        { label: 'Dashboard', path: '/dashboard', icon: 'ri-dashboard-line' },
        { label: 'Leads',     path: '/leads',     icon: 'ri-funnel-line' },
        { label: 'Customers', path: '/customers', icon: 'ri-contacts-line' },
        { label: 'Calendar',  path: '/calendar',  icon: 'ri-calendar-2-line' },
        { label: 'Reminders', path: '/reminders', icon: 'ri-calendar-check-line' },
    ];

    // Sections are tagged by `surface` so each header surface only shows what belongs there:
    //   'apps'    → desktop Apps mega-menu + mobile More sheet
    //   'account' → ProfileDropdown (avatar) + mobile More sheet
    //   'help'    → desktop Help icon dropdown + mobile More sheet
    const moreSections = isAdmin ? [
        { surface: 'apps',    title: 'Operations', items: [
            { label: 'Support Tickets',   icon: 'ri-customer-service-2-line', path: '/admin/support-tickets' },
            { label: 'Plan Upgrades',     icon: 'ri-vip-crown-line',          path: '/admin/plan-upgrade-requests' },
            { label: 'Contact Enquiries', icon: 'ri-questionnaire-line',      path: '/admin/contact-enquiries' },
        ]},
        { surface: 'account', title: 'Finance', items: [
            { label: 'Invoices', icon: 'ri-bill-line', path: '/admin/invoices' },
        ]},
        { surface: 'help',    title: 'Help', items: [
            { label: 'Admin Guide', icon: 'ri-book-open-line', path: '/admin/docs' },
        ]},
    ] : [
        { surface: 'apps',    title: 'Automate', items: [
            { label: 'Sequences',       icon: 'ri-mail-send-line', path: '/sequences' },
            { label: 'Email Templates', icon: 'ri-mail-open-line', path: '/email-templates' },
        ]},
        { surface: 'apps',    title: 'Capture', items: [
            { label: 'Lead Widgets', icon: 'ri-code-box-line',      path: '/widgets' },
            { label: 'Lead Forms',   icon: 'ri-file-list-3-line',   path: '/forms' },
            { label: 'Bookings',     icon: 'ri-calendar-check-line', path: '/bookings' },
            { label: 'AI Website',   icon: 'ri-global-line',        path: '/my-websites' },
        ]},
        { surface: 'apps',    title: 'Grow', items: [
            { label: 'Analytics',    icon: 'ri-bar-chart-line',           path: '/analytics' },
            { label: 'Affiliate',    icon: 'ri-money-dollar-circle-line', path: '/affiliate' },
            { label: 'Refer & Earn', icon: 'ri-gift-2-line',              path: '/referral' },
        ]},
        { surface: 'apps',    title: 'Get Started', items: [
            { label: 'Getting Started', icon: 'ri-rocket-2-line', path: '/getting-started' },
        ]},
        { surface: 'account', title: 'Workspace', items: [
            { label: 'Team',       icon: 'ri-team-line',       path: '/team' },
            { label: 'Settings',   icon: 'ri-settings-3-line', path: '/settings' },
            { label: 'Onboarding', icon: 'ri-compass-3-line',  path: '/onboarding' },
        ]},
        { surface: 'account', title: 'Billing', items: [
            { label: 'Plans',    icon: 'ri-vip-crown-line',      path: '/plans' },
            { label: 'Billing',  icon: 'ri-secure-payment-line', path: '/billing' },
            { label: 'Invoices', icon: 'ri-bill-line',           path: '/invoices' },
        ]},
        { surface: 'help',    title: 'Help', items: [
            { label: 'User Guide',      icon: 'ri-book-open-line',          path: '/docs' },
            { label: 'Support Tickets', icon: 'ri-customer-service-2-line', path: '/support-tickets' },
            { label: 'Help Center',     icon: 'ri-question-line',           path: '/help' },
        ]},
    ];

    const appsSections = moreSections.filter(s => s.surface === 'apps');
    const helpSections = moreSections.filter(s => s.surface === 'help');
    // Flat list — used for active-state detection on the mobile More tab.
    const moreItems = moreSections.flatMap((s) => s.items);

    // Visual indicator for Apps mega-menu
    const hasAppsItems = appsSections.some(s => s.items.length > 0);

    const [moreDropdown, setMoreDropdown] = useState(false);      // desktop "Apps" mega-menu
    const [helpDropdown, setHelpDropdown] = useState(false);      // desktop Help icon dropdown
    const [mobileMoreSheet, setMobileMoreSheet] = useState(false);
    const toggleMoreDropdown = () => { setMoreDropdown(v => !v); setHelpDropdown(false); };
    const toggleHelpDropdown = () => { setHelpDropdown(v => !v); setMoreDropdown(false); };

    // Close menus on route change
    useEffect(() => {
        setMobileMoreSheet(false);
        setMoreDropdown(false);
        setHelpDropdown(false);
    }, [location.pathname]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (e.target.closest('[data-more-dropdown]') === null) setMoreDropdown(false);
            if (e.target.closest('[data-help-dropdown]') === null) setHelpDropdown(false);
        };
        if (moreDropdown || helpDropdown) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [moreDropdown, helpDropdown]);

    const isActiveTab = (tab) => {
        if (tab.exact) {
            return location.pathname === tab.path;
        }
        return location.pathname.startsWith(tab.path);
    };

    return (
        <React.Fragment>
            <header id="page-topbar" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1002,
                background: 'var(--vz-topbar-bg, #ffffff)',
                borderBottom: '1px solid var(--vz-border-color, #f0f0f0)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                height: '60px'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '100%',
                    paddingLeft: '1.5rem',
                    paddingRight: '1.5rem'
                }}>
                    {/* Left Section: Logo + Brand + Add Service Button */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1.5rem',
                        minWidth: 'fit-content'
                    }}>
                        <Link to="/" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            textDecoration: 'none',
                            transition: 'opacity 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                            <img src={logoModern} alt="LeadWix" style={{
                                height: '32px',
                                width: '32px'
                            }} />
                            <span style={{
                                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
                                fontSize: '1rem',
                                fontWeight: '700',
                                color: 'var(--vz-heading-color, #1a1a1f)',
                                letterSpacing: '-0.4px'
                            }}>
                                LeadWix
                            </span>
                        </Link>
                    </div>

                    {/* Center Section: Navigation Tabs - Desktop Only */}
                    <nav style={{
                        display: 'none',
                        gap: '0.5rem',
                        alignItems: 'center',
                        flex: 1,
                        marginLeft: '3rem'
                    }}
                    className="d-lg-flex"
                    >
                        {navTabs.map((tab) => {
                            const active = isActiveTab(tab);
                            return (
                                <Link
                                    key={tab.path}
                                    to={tab.path}
                                    style={{
                                        padding: '0.75rem 1rem',
                                        textDecoration: 'none',
                                        fontSize: '0.9rem',
                                        fontWeight: active ? '600' : '500',
                                        color: active ? '#7c3aed' : '#5a5a6e',
                                        transition: 'all 0.2s ease',
                                        borderBottom: active ? '2px solid #7c3aed' : '2px solid transparent',
                                        paddingBottom: active ? 'calc(0.75rem - 2px)' : '0.75rem',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!active) {
                                            e.currentTarget.style.color = '#7c3aed';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!active) {
                                            e.currentTarget.style.color = '#5a5a6e';
                                        }
                                    }}
                                >
                                    {tab.label}
                                </Link>
                            );
                        })}

                        {/* More Dropdown */}
                        <div style={{ position: 'relative', display: 'inline-block' }} data-more-dropdown="true">
                            <button
                                onClick={toggleMoreDropdown}
                                style={{
                                    padding: '0.75rem 1rem',
                                    textDecoration: 'none',
                                    fontSize: '0.9rem',
                                    fontWeight: moreDropdown ? '600' : '500',
                                    color: moreDropdown ? '#7c3aed' : '#5a5a6e',
                                    transition: 'all 0.2s ease',
                                    borderBottom: moreDropdown ? '2px solid #7c3aed' : '2px solid transparent',
                                    paddingBottom: moreDropdown ? 'calc(0.75rem - 2px)' : '0.75rem',
                                    cursor: 'pointer',
                                    border: 'none',
                                    background: 'transparent',
                                    fontFamily: 'inherit',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.35rem',
                                    position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                    if (!moreDropdown) {
                                        e.currentTarget.style.color = '#7c3aed';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!moreDropdown) {
                                        e.currentTarget.style.color = '#5a5a6e';
                                    }
                                }}
                            >
                                <span>Apps</span>
                                {hasAppsItems && !moreDropdown && (
                                    <span style={{
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        backgroundColor: '#7c3aed',
                                        display: 'inline-block',
                                        position: 'absolute',
                                        top: '8px',
                                        right: '0px'
                                    }}></span>
                                )}
                                <i className={`ri-chevron-${moreDropdown ? 'up' : 'down'}-line`} style={{ fontSize: '1rem' }}></i>
                            </button>

                            {/* Dropdown Mega-menu */}
                            {moreDropdown && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    backgroundColor: 'var(--vz-topbar-bg, #ffffff)',
                                    border: '1px solid var(--vz-border-color, #f0f0f0)',
                                    borderRadius: '0.75rem',
                                    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.10)',
                                    width: 'min(720px, calc(100vw - 3rem))',
                                    marginTop: '0.5rem',
                                    zIndex: 1000,
                                    overflow: 'hidden',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    padding: '1rem',
                                    gap: '0.5rem 1.25rem',
                                }}>
                                    {appsSections.map((section) => (
                                        <div key={section.title}>
                                            <div style={{
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.06em',
                                                color: '#9094a8',
                                                padding: '0.4rem 0.5rem 0.5rem',
                                            }}>{section.title}</div>
                                            {section.items.map((item) => {
                                                const active = location.pathname.startsWith(item.path);
                                                return (
                                                    <Link
                                                        key={item.path}
                                                        to={item.path}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '0.6rem',
                                                            padding: '0.5rem 0.5rem',
                                                            color: active ? '#7c3aed' : '#3a3a4a',
                                                            background: active ? '#f3f0ff' : 'transparent',
                                                            textDecoration: 'none',
                                                            borderRadius: '0.5rem',
                                                            fontSize: '0.875rem',
                                                            fontWeight: active ? 600 : 500,
                                                            transition: 'background 0.15s ease, color 0.15s ease',
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (!active) {
                                                                e.currentTarget.style.background = '#f5f5f7';
                                                                e.currentTarget.style.color = '#7c3aed';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!active) {
                                                                e.currentTarget.style.background = 'transparent';
                                                                e.currentTarget.style.color = '#3a3a4a';
                                                            }
                                                        }}
                                                        onClick={() => setMoreDropdown(false)}
                                                    >
                                                        <i className={item.icon} style={{ fontSize: '1rem', color: active ? '#7c3aed' : '#7a7a8c', width: 18, textAlign: 'center' }}></i>
                                                        <span>{item.label}</span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </nav>

                    {/* Right Section: Icons - LoopAI Minimalist Style */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.75rem',
                        minWidth: 'fit-content',
                        height: '60px'
                    }}>
                        {/* Help icon dropdown — desktop only */}
                        <div className="d-none d-lg-block" style={{ position: 'relative' }} data-help-dropdown="true">
                            <button
                                onClick={toggleHelpDropdown}
                                title="Help"
                                aria-label="Help"
                                style={{
                                    border: 'none',
                                    background: helpDropdown ? 'var(--vz-topbar-search-bg, #f5f5f7)' : 'transparent',
                                    cursor: 'pointer',
                                    padding: '0.45rem',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background 0.2s',
                                    fontSize: '1.2rem',
                                    color: helpDropdown ? '#7c3aed' : 'var(--vz-header-item-color, #5a5a6e)',
                                }}
                                onMouseEnter={(e) => { if (!helpDropdown) e.currentTarget.style.background = 'var(--vz-topbar-search-bg, #f5f5f7)'; }}
                                onMouseLeave={(e) => { if (!helpDropdown) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <i className="ri-question-line" />
                            </button>
                            {helpDropdown && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    marginTop: '0.5rem',
                                    minWidth: '220px',
                                    backgroundColor: 'var(--vz-topbar-bg, #ffffff)',
                                    border: '1px solid var(--vz-border-color, #f0f0f0)',
                                    borderRadius: '0.75rem',
                                    boxShadow: '0 12px 32px rgba(0,0,0,0.10)',
                                    padding: '0.5rem',
                                    zIndex: 1000,
                                }}>
                                    {helpSections.flatMap(s => s.items).map((item) => {
                                        const active = location.pathname.startsWith(item.path);
                                        return (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                onClick={() => setHelpDropdown(false)}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                                                    padding: '0.5rem 0.6rem', borderRadius: '0.5rem',
                                                    textDecoration: 'none', fontSize: '0.875rem',
                                                    color: active ? '#7c3aed' : '#3a3a4a',
                                                    background: active ? '#f3f0ff' : 'transparent',
                                                    fontWeight: active ? 600 : 500,
                                                }}
                                                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#f5f5f7'; }}
                                                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                                            >
                                                <i className={item.icon} style={{ fontSize: '1rem', width: 18, textAlign: 'center', color: active ? '#7c3aed' : '#7a7a8c' }} />
                                                <span>{item.label}</span>
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Dark Mode Toggle */}
                        <button
                            onClick={() => {
                                const newMode = layoutModeType === layoutModeTypes.DARKMODE
                                    ? layoutModeTypes.LIGHTMODE
                                    : layoutModeTypes.DARKMODE;
                                onChangeLayoutMode(newMode);
                                localStorage.setItem('layoutMode', newMode);
                            }}
                            title={layoutModeType === layoutModeTypes.DARKMODE ? 'Light mode' : 'Dark mode'}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                padding: '0.45rem',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background 0.2s',
                                fontSize: '1.2rem',
                                color: 'var(--vz-header-item-color, #5a5a6e)',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--vz-topbar-search-bg, #f5f5f7)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            <i className={layoutModeType === layoutModeTypes.DARKMODE ? 'ri-sun-line' : 'ri-moon-line'} />
                        </button>

                        {/* Notification Bell */}
                        <NotificationBell />

                        {/* Profile Dropdown */}
                        <ProfileDropdown />
                    </div>
                </div>
            </header>

            {/* ── Mobile Bottom Tab Bar (Zoho-style, hidden on desktop) ── */}
                <div className="d-lg-none" style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1001,
                    background: 'var(--vz-topbar-bg, #ffffff)',
                    borderTop: '1px solid var(--vz-border-color, #ebebeb)',
                    boxShadow: '0 -2px 12px rgba(0,0,0,0.07)',
                    display: 'flex',
                    height: 'calc(58px + env(safe-area-inset-bottom, 0px))',
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                    transform: 'translateZ(0)',
                    WebkitTransform: 'translateZ(0)',
                    willChange: 'transform',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                }}>
                    {(isAdmin ? [
                        { label: 'Overview', path: '/admin', icon: 'ri-dashboard-line', activeIcon: 'ri-dashboard-fill', exact: true },
                        { label: 'Users', path: '/admin/users', icon: 'ri-group-line', activeIcon: 'ri-group-fill' },
                        { label: 'Payments', path: '/admin/payments', icon: 'ri-wallet-line', activeIcon: 'ri-wallet-fill' },
                    ] : [
                        { label: 'Dashboard', path: '/dashboard', icon: 'ri-dashboard-line', activeIcon: 'ri-dashboard-fill' },
                        { label: 'Leads', path: '/leads', icon: 'ri-funnel-line', activeIcon: 'ri-funnel-fill' },
                        { label: 'Customers', path: '/customers', icon: 'ri-contacts-line', activeIcon: 'ri-contacts-fill' },
                        { label: 'Reminders', path: '/reminders', icon: 'ri-calendar-check-line', activeIcon: 'ri-calendar-check-fill' },
                    ]).map((tab) => {
                        const active = isActiveTab(tab);
                        return (
                            <Link
                                key={tab.path}
                                to={tab.path}
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '2px',
                                    textDecoration: 'none',
                                    color: active ? '#7c3aed' : '#8e8ea1',
                                    transition: 'color 0.15s ease',
                                    WebkitTapHighlightColor: 'transparent',
                                    position: 'relative',
                                }}
                            >
                                {active && (
                                    <span style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: '32px',
                                        height: '3px',
                                        borderRadius: '0 0 3px 3px',
                                        background: '#7c3aed',
                                    }} />
                                )}
                                <i className={active ? tab.activeIcon : tab.icon} style={{ fontSize: '1.35rem' }} />
                                <span style={{ fontSize: '0.65rem', fontWeight: active ? '600' : '500', letterSpacing: '0.01em' }}>
                                    {tab.label}
                                </span>
                            </Link>
                        );
                    })}

                    {/* More tab */}
                    <button
                        onClick={() => setMobileMoreSheet(s => !s)}
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '2px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: mobileMoreSheet ? '#7c3aed' : '#8e8ea1',
                            transition: 'color 0.15s ease',
                            WebkitTapHighlightColor: 'transparent',
                            padding: 0,
                            position: 'relative',
                        }}
                    >
                        {moreItems.some(item => location.pathname.startsWith(item.path)) && !mobileMoreSheet && (
                            <span style={{
                                position: 'absolute',
                                top: 0,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '32px',
                                height: '3px',
                                borderRadius: '0 0 3px 3px',
                                background: '#7c3aed',
                            }} />
                        )}
                        <i className={mobileMoreSheet ? 'ri-close-line' : 'ri-more-2-fill'} style={{ fontSize: '1.35rem' }} />
                        <span style={{ fontSize: '0.65rem', fontWeight: '500' }}>More</span>
                    </button>
                </div>

            {/* ── More Sheet overlay ── */}
            {mobileMoreSheet && (
                <>
                    {/* Backdrop */}
                    <div
                        className="d-lg-none"
                        onClick={() => setMobileMoreSheet(false)}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 1000,
                            background: 'rgba(0,0,0,0.35)',
                        }}
                    />
                    {/* Sheet */}
                    <div className="d-lg-none" style={{
                        position: 'fixed',
                        bottom: '58px',
                        left: 0,
                        right: 0,
                        zIndex: 1001,
                        background: 'var(--vz-topbar-bg, #ffffff)',
                        borderRadius: '16px 16px 0 0',
                        boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
                        padding: '8px 0 8px',
                        maxHeight: '70vh',
                        overflowY: 'auto',
                    }}>
                        {/* Handle */}
                        <div style={{
                            width: '36px', height: '4px',
                            background: '#e0e0e0', borderRadius: '2px',
                            margin: '0 auto 12px',
                        }} />
                        {moreSections.map((section) => (
                            <div key={section.title} style={{ paddingBottom: '4px' }}>
                                <div style={{
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    color: '#9094a8',
                                    padding: '10px 20px 6px',
                                }}>{section.title}</div>
                                {section.items.map((item) => {
                                    const active = location.pathname.startsWith(item.path);
                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            onClick={() => setMobileMoreSheet(false)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '14px',
                                                padding: '12px 20px',
                                                color: active ? '#7c3aed' : '#2d2d3a',
                                                textDecoration: 'none',
                                                background: active ? '#f0f1ff' : 'transparent',
                                                fontWeight: active ? '600' : '500',
                                                fontSize: '0.95rem',
                                            }}
                                        >
                                            <span style={{
                                                width: '36px', height: '36px',
                                                borderRadius: '10px',
                                                background: active ? '#e8eaff' : '#f5f5f8',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                                <i className={item.icon} style={{ fontSize: '1.1rem', color: active ? '#7c3aed' : '#5a5a6e' }} />
                                            </span>
                                            <span>{item.label}</span>
                                            <i className="ri-arrow-right-s-line" style={{ marginLeft: 'auto', color: '#c0c0cc', fontSize: '1.1rem' }} />
                                        </Link>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </React.Fragment>
    );
};

export default Header;
